/**
 * /app/dashboard — Inicio del workspace (layout Stitch / clinical, paridad Booking Hub).
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ClinicalActivityFeed, ClinicalOrderRow, ClinicalPageShell, OccupancyGauge } from "@/components/clinical";
import type { ActivityItem } from "@/components/clinical";
import {
  DashboardCard,
  CrudEmptyState,
  CrudShellHeader,
  Loading,
  Panel,
  StatCard,
} from "@/components/ds";
import { Button } from "@/components/ui/button";
import {
  Users,
  Activity,
  ClipboardList,
  ExternalLink,
  FileText,
  Inbox,
  Loader2,
  Package,
  Scale,
  ShoppingBasket,
  Wallet,
  Zap,
} from "lucide-react";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantContext } from "@/lib/tenant-context";
import { useReportingLocationScope } from "@/hooks/use-reporting-location-scope";
import { formatDistanceToNow, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { useIsMobile } from "@/hooks/use-mobile";
import { DashboardTour } from "@/components/ProductTour";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { ActivationChecklist } from "@/components/ActivationChecklist";
import { KycHomeBanner } from "@/components/KycHomeBanner";
import { ProviderHealthPanel } from "@/components/ProviderHealthPanel";
import { HealthScoreCard, type HealthScoreCardData } from "@/components/HealthScoreCard";
import { SampleDataBanner } from "@/components/SampleDataBanner";
import { FirstSaleCelebration } from "@/components/FirstSaleCelebration";
import { useAccountPlanSummary } from "@/hooks/use-account-plan-summary";
import { DashboardReservationsCard } from "@/components/DashboardReservationsCard";
import { DashboardServiciosCard, type UpcomingBooking } from "@/components/DashboardServiciosCard";
import { localCalendarDateIso, addLocalCalendarDays } from "@/lib/format-date";
import { toast } from "sonner";
import type { OrderStatus } from "@/domain/sm-models";
import { ORDER_STATUS_LABELS } from "@/domain/sm-models";
import { useAppDialogs } from "@/components/ui/app-dialogs";

export const Route = createFileRoute("/app/dashboard")({
  component: DashboardHome,
  head: () => ({ meta: [{ title: "Inicio — Structura Market" }] }),
});

interface RecentOrder {
  id: string;
  customerName: string;
  status: OrderStatus;
  precioFinal: number | null;
  precioEstimado: number | null;
  createdAt: string;
}

interface DashboardStats {
  ordersToday: number;
  ordersPending: number;
  ordersMonth: number;
  ordersWeek: number;
  ordersPrevWeek: number;
  revenueMonth: number;
  fulfillmentRate: number;
  productsCount: number;
  reservationsToday?: number;
  reservationsTotal?: number;
  newCustomersMonth?: number;
  returningCustomersMonth?: number;
  returningRateMonth?: number;
  lifetimeOrders?: number;
  healthScore?: HealthScoreCardData;
  recentOrders: RecentOrder[];
}

function formatMoney(amount: number) {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(amount);
}

function activityVariant(status: OrderStatus): ActivityItem["variant"] {
  if (status === "CANCELADO") return "cancel";
  if (status === "ENTREGADO" || status === "EN_CAMINO" || status === "LISTO" || status === "LISTO_RETIRO") {
    return "delivery";
  }
  if (status === "PAGADO" || status === "PENDIENTE_PAGO") return "payment";
  if (status === "AJUSTADO_PESADO" || status === "EN_PREPARACION") return "update";
  return "order";
}

function DashboardHome() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const tid = useWorkspaceTenantId();
  const { tenantSlug } = useTenantContext();
  const storeHref = tenantSlug ? `/market/${tenantSlug}` : null;

  const { businessProfile, productFlags: ctxFlags } = useTenantContext();
  const { planLabel, subStatus, validUntil } = useAccountPlanSummary();
  // Inline niche gate (= businessAllowsMerchantPersonalization) — no unbound helper call.
  const merchantEnabled =
    ctxFlags?.integrations_merchant === true &&
    businessProfile === "fashion";
  const isGastroProfile = ["restaurant", "fast_food", "cafe"].includes(businessProfile ?? "");
  const showReservationsCard = isGastroProfile;
  // SCRUM-406: /app/dashboard era 100% e-commerce, sin ninguna referencia a
  // turnos — paridad con el dashboard de Booking Hub para el nicho servicios.
  const showServiciosCard = businessProfile === "servicios";

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [serviciosStats, setServiciosStats] = useState<{ today: number; pendingConfirm: number; thisWeek: number; upcoming: UpcomingBooking[] } | null>(null);
  const { confirm } = useAppDialogs();
  const [loading, setLoading] = useState(true);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [showSampleBanner, setShowSampleBanner] = useState(false);
  const [clearingDemo, setClearingDemo] = useState(false);
  // SCRUM-74: el switcher global de sucursal (montado en app.tsx) ahora también
  // filtra el dashboard — antes cambiar de sucursal no afectaba estos KPIs.
  // Fase 8: reportLocationId permite ampliar la vista a "todas" (owner/super_admin/
  // staff sin scope) sin tocar la sucursal operativa activa del sidebar.
  const { reportLocationId, setReportLocationId, canViewAll, locations } = useReportingLocationScope();

  useEffect(() => {
    if (!tid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const statsPath = reportLocationId
      ? `/api/dashboard/stats?locationId=${encodeURIComponent(reportLocationId)}`
      : "/api/dashboard/stats";
    void Promise.all([
      apiGetJson<DashboardStats>(statsPath, tid),
      apiGetJson<{ count: number }>("/api/inventory/alerts", tid).catch(() => ({ count: 0 })),
    ])
      .then(([s, alerts]) => {
        setStats(s);
        setLowStockCount(alerts.count ?? 0);
        // Mostrar el banner de datos de muestra si no hay productos cargados
        setShowSampleBanner((s.productsCount ?? 0) === 0);
      })
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [tid, reportLocationId]);

  // SCRUM-406: KPIs de turnos para el nicho servicios, reusando /api/service-bookings
  // (dateFrom/dateTo ya soportado) — sin endpoint nuevo.
  useEffect(() => {
    if (!tid || !showServiciosCard) return;
    const todayIso = localCalendarDateIso();
    const weekEndIso = addLocalCalendarDays(6);
    void Promise.all([
      apiGetJson<{ bookings: Array<{ id: string; customerName: string; bookingDate: string; startTime: string; status: string }> }>(
        `/api/service-bookings?dateFrom=${todayIso}&dateTo=${weekEndIso}&orderBy=booking_date&orderDir=asc`,
        tid,
      ),
      apiGetJson<{ total: number }>(`/api/service-bookings?status=pending&dateFrom=${todayIso}`, tid).catch(() => ({ total: 0 })),
    ])
      .then(([week, pending]) => {
        const items = week.bookings ?? [];
        const todayCount = items.filter((b) => b.bookingDate === todayIso).length;
        const upcoming = items
          .filter((b) => ["pending", "confirmed"].includes(b.status))
          .map((b) => ({ id: b.id, customerName: b.customerName, bookingDate: b.bookingDate, startTime: b.startTime }));
        setServiciosStats({
          today: todayCount,
          pendingConfirm: pending.total ?? 0,
          thisWeek: items.length,
          upcoming,
        });
      })
      .catch(() => setServiciosStats(null));
  }, [tid, showServiciosCard]);

  const clearDemoData = async () => {
    if (!tid) return;
    if (!(await confirm({
      title: "¿Seguro que querés eliminar todos los productos y categorías?",
      description: "Esta acción no se puede deshacer.",
      destructive: true,
      confirmLabel: "Eliminar",
    }))) return;
    setClearingDemo(true);
    try {
      await apiSendJson("/api/tenant/seed-sample", "DELETE", undefined, tid);
      toast.success("Datos de muestra eliminados. Tu catálogo está limpio.");
      setShowSampleBanner(true);
      setStats((s) => (s ? { ...s, productsCount: 0 } : s));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo limpiar el catálogo");
    } finally {
      setClearingDemo(false);
    }
  };

  const weekGrowthPct = useMemo(() => {
    const week = stats?.ordersWeek ?? 0;
    const prev = stats?.ordersPrevWeek ?? 0;
    if (prev === 0) return week > 0 ? 100 : 0;
    return Math.round(((week - prev) / prev) * 1000) / 10;
  }, [stats?.ordersWeek, stats?.ordersPrevWeek]);

  const pendingOrders = useMemo(
    () =>
      (stats?.recentOrders ?? []).filter((o) =>
        ["PENDIENTE", "AJUSTADO_PESADO", "PENDIENTE_PAGO", "CONFIRMADO"].includes(o.status),
      ),
    [stats?.recentOrders],
  );

  const activityItems = useMemo((): ActivityItem[] => {
    return (stats?.recentOrders ?? []).slice(0, 8).map((o) => {
      const when = formatDistanceToNow(parseISO(o.createdAt), { addSuffix: true, locale: es });
      const amount = o.precioFinal ?? o.precioEstimado;
      const variant = activityVariant(o.status);
      let title = "Nuevo pedido";
      if (variant === "cancel") title = "Pedido cancelado";
      else if (variant === "payment") title = o.status === "PAGADO" ? "Pago recibido" : "Esperando pago";
      else if (variant === "delivery") title = "Pedido en curso";
      else if (variant === "update") title = "Estado actualizado";

      const subtitle =
        amount != null
          ? `${o.customerName} · ${formatMoney(amount)} · ${when}`
          : `${o.customerName} · ${ORDER_STATUS_LABELS[o.status]} · ${when}`;

      return { id: o.id, variant, title, subtitle };
    });
  }, [stats?.recentOrders]);

  const busyInsight = useMemo(() => {
    const pending = stats?.ordersPending ?? 0;
    const today = stats?.ordersToday ?? 0;
    if (pending > 0) {
      return `Tenés ${pending} pedido${pending > 1 ? "s" : ""} que requieren acción. Revisá Pesaje y Pedidos para confirmar o cobrar antes del cierre del día.`;
    }
    if (today === 0) {
      return "Todavía no entraron pedidos hoy. Compartí el link de tu tienda en redes o WhatsApp para activar ventas.";
    }
    return `Llevás ${today} pedido${today > 1 ? "s" : ""} hoy y ${stats?.ordersWeek ?? 0} en la última semana. Revisá Métricas para ver qué productos convierten mejor.`;
  }, [stats?.ordersPending, stats?.ordersToday, stats?.ordersWeek]);

  if (loading) {
    return (
      <div>
        {!isMobile ? (
          <CrudShellHeader title="Panel de control" subtitle="Esto es lo que está pasando hoy en tu negocio." />
        ) : null}
        <Loading />
      </div>
    );
  }

  const s = stats;

  const showPesaje = (["market", "pharmacy", "dietetics", "bazar", "fast_food"] as const).includes(
    businessProfile as "market" | "pharmacy" | "dietetics" | "bazar" | "fast_food",
  );

  if (isMobile) {
    return (
      <div className="min-h-screen bg-background pb-8">
        <DashboardTour businessProfile={businessProfile} merchantEnabled={merchantEnabled} />
        <div className="space-y-4 px-4 pt-4">
          <MerchantExperienceBar screenId="dashboard" />
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col gap-1">
              <h1 className="text-headline-md font-bold text-foreground">Inicio</h1>
              <p className="text-body-md text-muted-foreground">Resumen de hoy</p>
            </div>
            {/* FM-FTE-002: Siempre visible, primer punto de contacto para el owner */}
            {storeHref && (
              <a
                href={storeHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex shrink-0 items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Ver tienda
              </a>
            )}
          </div>
          <ActivationChecklist />
          <KycHomeBanner />
          <HealthScoreCard data={s?.healthScore} />
          {tid ? (
            <FirstSaleCelebration
              tenantId={tid}
              tenantSlug={tenantSlug}
              lifetimeOrders={s?.lifetimeOrders ?? 0}
            />
          ) : null}
          <ProviderHealthPanel />
          {showSampleBanner && (
            <SampleDataBanner
              onSeeded={() => {
                setShowSampleBanner(false);
                // Refrescar stats para que se vean los productos nuevos
                if (tid) {
                  void apiGetJson<DashboardStats>("/api/dashboard/stats", tid)
                    .then(setStats)
                    .catch(() => null);
                }
              }}
            />
          )}
          {lowStockCount > 0 && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
              Stock bajo en {lowStockCount} producto{lowStockCount > 1 ? "s" : ""}.{" "}
              <Link to="/app/inventario" search={{} as never} className="font-medium text-primary underline">
                Revisar inventario
              </Link>
            </div>
          )}
          {showReservationsCard ? (
            <DashboardReservationsCard
              compact
              reservationsToday={s?.reservationsToday ?? 0}
              reservationsTotal={s?.reservationsTotal ?? 0}
            />
          ) : null}
          {showServiciosCard ? (
            <DashboardServiciosCard
              compact
              today={serviciosStats?.today ?? 0}
              pendingConfirm={serviciosStats?.pendingConfirm ?? 0}
              thisWeek={serviciosStats?.thisWeek ?? 0}
              upcoming={serviciosStats?.upcoming ?? []}
            />
          ) : null}
          <div className="grid grid-cols-2 gap-3" data-tour="dashboard-mobile-kpis">
            <div className="glass-card col-span-2 rounded-xl p-4">
              <p className="text-label-md text-muted-foreground">Pedidos de hoy</p>
              <p className="text-headline-lg-mobile font-bold text-primary">{s?.ordersToday ?? 0}</p>
            </div>
            <div className="glass-card rounded-xl p-4">
              <p className="text-label-md text-muted-foreground">Semana</p>
              <p className="text-headline-md font-bold text-foreground">{s?.ordersWeek ?? 0}</p>
            </div>
            <div className="glass-card rounded-xl p-4">
              <p className="text-label-md text-muted-foreground">Pendientes</p>
              <p className="text-headline-md font-bold text-amber-400">{s?.ordersPending ?? 0}</p>
            </div>
            <div className="glass-card col-span-2 rounded-xl p-4">
              <p className="text-label-md text-muted-foreground">Recurrentes este mes</p>
              <p className="text-headline-md font-bold text-foreground">
                {s?.returningRateMonth ?? 0}%
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({s?.returningCustomersMonth ?? 0} clientes)
                </span>
              </p>
            </div>
          </div>
          <Button className="w-full gap-2" asChild data-tour="dashboard-new-order">
            <Link to="/app/pedidos">
              <ClipboardList className="h-4 w-4" />
              Ver pedidos
            </Link>
          </Button>
          <section className="glass-card rounded-xl p-4" data-tour="dashboard-mobile-upcoming">
            <h3 className="mb-3 text-body-lg font-semibold text-foreground">Pedidos pendientes</h3>
            {pendingOrders.length === 0 ? (
              <CrudEmptyState icon={Inbox} title="No hay pedidos pendientes" className="border-none bg-transparent p-0 py-4" />
            ) : (
              <div className="space-y-2">
                {pendingOrders.slice(0, 5).map((o) => (
                  <ClinicalOrderRow
                    key={o.id}
                    createdAt={o.createdAt}
                    customerName={o.customerName}
                    status={o.status}
                    precioFinal={o.precioFinal}
                    precioEstimado={o.precioEstimado}
                    onClick={() => void navigate({ to: "/app/pedidos" })}
                  />
                ))}
              </div>
            )}
          </section>
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
            <p className="mb-1 text-xs font-bold uppercase tracking-wider text-primary">Insight</p>
            <p className="text-sm text-muted-foreground">{busyInsight}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <DashboardTour />
      <CrudShellHeader
        title="Panel de control"
        description="Esto es lo que está pasando hoy en tu negocio."
        actions={
          <div className="flex items-center gap-2">
            {canViewAll && locations.length > 1 ? (
              <select
                aria-label="Vista de reporting por sucursal"
                className="hidden h-8 rounded-full border border-border bg-muted/60 px-3 text-xs font-medium text-muted-foreground sm:block"
                value={reportLocationId ?? ""}
                onChange={(e) => setReportLocationId(e.target.value || null)}
              >
                <option value="">Todas las sucursales</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            ) : null}
            {/* Badge de plan — compacto, lleva a suscripción */}
            <a
              href="/app/suscripcion"
              className="hidden items-center gap-1.5 rounded-full border border-border bg-muted/60 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted sm:flex"
            >
              <span className={`h-1.5 w-1.5 rounded-full ${subStatus === "active" ? "bg-green-500" : "bg-amber-400"}`} />
              {planLabel}
              {validUntil ? <span className="opacity-60">· hasta {validUntil.slice(0, 10).split("-").reverse().join("/")}</span> : null}
            </a>
            {storeHref && (
              <Button variant="ghost" size="sm" className="gap-1.5" asChild>
                <a href={storeHref} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" /> Ver mi tienda
                </a>
              </Button>
            )}
            {showPesaje && (
              <Button variant="outline" size="sm" className="gap-1.5" asChild>
                <a href="/app/pesaje"><Scale className="h-4 w-4" /> Pesaje</a>
              </Button>
            )}
            <Button size="sm" className="gap-1.5" data-tour="dashboard-new-order" asChild>
              <a href="/app/pedidos"><ClipboardList className="h-4 w-4" /> Ver pedidos</a>
            </Button>
          </div>
        }
      />

      <ClinicalPageShell offset="none" className="pt-6">
        <MerchantExperienceBar screenId="dashboard" />
        <ActivationChecklist className="mb-2" />
        <KycHomeBanner className="mb-4" />
        <HealthScoreCard data={s?.healthScore} className="mb-4" />
        {tid ? (
          <FirstSaleCelebration
            tenantId={tid}
            tenantSlug={tenantSlug}
            lifetimeOrders={s?.lifetimeOrders ?? 0}
          />
        ) : null}
        <ProviderHealthPanel className="mb-4" />

        {showSampleBanner && (
          <SampleDataBanner
            className="mb-4"
            onSeeded={() => {
              setShowSampleBanner(false);
              if (tid) {
                void apiGetJson<DashboardStats>("/api/dashboard/stats", tid)
                  .then(setStats)
                  .catch(() => null);
              }
            }}
          />
        )}

        {lowStockCount > 0 && (
          <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-foreground">
              {lowStockCount} producto{lowStockCount > 1 ? "s" : ""} con stock bajo o agotado.
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/app/inventario" search={{} as never}>Ver inventario</Link>
            </Button>
          </div>
        )}

        {showReservationsCard ? (
          <div className="mt-4">
            <DashboardReservationsCard
              reservationsToday={s?.reservationsToday ?? 0}
              reservationsTotal={s?.reservationsTotal ?? 0}
            />
          </div>
        ) : null}

        {showServiciosCard ? (
          <div className="mt-4">
            <DashboardServiciosCard
              today={serviciosStats?.today ?? 0}
              pendingConfirm={serviciosStats?.pendingConfirm ?? 0}
              thisWeek={serviciosStats?.thisWeek ?? 0}
              upcoming={serviciosStats?.upcoming ?? []}
            />
          </div>
        ) : null}

        {/* FM-FTE-001: Empty state destacado cuando no hay actividad en absoluto */}
        {(s?.ordersToday ?? 0) === 0 && (s?.ordersWeek ?? 0) === 0 && !showSampleBanner && (
          <div className="mb-6 rounded-2xl border border-primary/20 bg-primary/5 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex flex-col gap-1.5">
              <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                Tu tienda está lista — ¡falta el primer pedido!
              </p>
              <p className="text-xs text-muted-foreground">
                Compartí el link de tu tienda para empezar a recibir pedidos reales.
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              {storeHref && (
                <Button asChild size="sm" variant="outline" className="gap-1.5">
                  <a href={storeHref} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5" />
                    Ver tienda
                  </a>
                </Button>
              )}
              <Button asChild size="sm" className="gap-1.5">
                <Link to="/app/productos">
                  <Package className="h-3.5 w-3.5" />
                  Agregar productos
                </Link>
              </Button>
              {(s?.productsCount ?? 0) > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5 text-muted-foreground hover:text-destructive"
                  disabled={clearingDemo}
                  onClick={clearDemoData}
                >
                  {clearingDemo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Limpiar datos de muestra
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-tour="dashboard-kpis">
          <StatCard
            label="Pedidos de hoy"
            value={s?.ordersToday ?? 0}
            trend={`${(s?.ordersToday ?? 0) > 0 ? "+" : ""}${Math.min(99, (s?.ordersToday ?? 0) * 4)}%`}
            icon={ShoppingBasket}
            variant="primary"
            progress={Math.min(100, ((s?.ordersToday ?? 0) / Math.max(1, s?.ordersWeek ?? 1)) * 100 * 4)}
          />
          <StatCard
            label="Crecimiento semanal"
            value={s?.ordersWeek ?? 0}
            trend={`${weekGrowthPct >= 0 ? "+" : ""}${weekGrowthPct}%`}
            trendUp={weekGrowthPct >= 0}
            icon={Activity}
            variant="secondary"
          />
          <StatCard
            label="Pagos pendientes"
            value={(s?.ordersPending ?? 0) > 0 ? s!.ordersPending : 0}
            trend={(s?.ordersPending ?? 0) > 0 ? String(s!.ordersPending) : undefined}
            trendUp={false}
            icon={Wallet}
            variant="warning"
          />
          <StatCard
            label="Clientes recurrentes"
            value={`${s?.returningRateMonth ?? 0}%`}
            trend={`${s?.returningCustomersMonth ?? 0} este mes`}
            trendUp={(s?.returningRateMonth ?? 0) > 0}
            icon={Users}
            variant="secondary"
          />
        </div>

        <div className="grid grid-cols-1 gap-stack-lg lg:grid-cols-3">
          <div className="lg:col-span-2" data-tour="dashboard-upcoming">
            <Panel
              title="Pedidos recientes"
              actions={
                <Link to="/app/pedidos" className="text-label-md font-medium text-primary hover:underline">
                  Ver todos
                </Link>
              }
            >
              <div className="space-y-3">
                {!s?.recentOrders?.length ? (
                  <CrudEmptyState icon={Inbox} title="No hay pedidos aún" />
                ) : (
                  s.recentOrders.map((o) => (
                    <ClinicalOrderRow
                      key={o.id}
                      createdAt={o.createdAt}
                      customerName={o.customerName}
                      status={o.status}
                      precioFinal={o.precioFinal}
                      precioEstimado={o.precioEstimado}
                      onClick={() => void navigate({ to: "/app/pedidos" })}
                    />
                  ))
                )}
              </div>
            </Panel>
          </div>

          <div className="space-y-stack-md" data-tour="dashboard-activity">
            <h3 className="text-headline-md font-semibold text-foreground">Actividad reciente</h3>
            <ClinicalActivityFeed items={activityItems} />
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-card-padding">
              <div className="mb-2 flex items-center gap-2 text-primary">
                <Zap className="h-4 w-4" />
                <span className="text-label-md font-bold uppercase tracking-wider">Insight rápido</span>
              </div>
              <p className="text-body-md leading-snug text-foreground">{busyInsight}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          <div className="glass-card flex items-center justify-between rounded-xl border-l-4 border-l-primary p-card-padding md:col-span-2">
            <div className="space-y-1">
              <h4 className="text-headline-md font-semibold text-foreground">Tasa de entrega</h4>
              <p className="text-body-md text-muted-foreground">Pedidos entregados este mes</p>
            </div>
            <OccupancyGauge value={s?.fulfillmentRate ?? 0} />
          </div>
          <DashboardCard
            to="/app/productos"
            icon={Package}
            title="Gestionar catálogo"
            subtitle={`${s?.productsCount ?? 0} productos activos`}
          />
          <DashboardCard
            to="/app/metrics"
            icon={FileText}
            title="Ver métricas"
            subtitle={
              s?.revenueMonth != null && s.revenueMonth > 0 ? `${formatMoney(s.revenueMonth)} este mes` : "Resumen mensual"
            }
          />
        </div>
      </ClinicalPageShell>
    </div>
  );
}
